import { Action } from "redux";

import { FormOpenMode, MenuItemType } from "@multiweb/types/contracts";
import { SagaWithReturnType } from "@multiweb/types/react";
import { Event } from "../../../Global/Base/Events";
import { FormResult } from "./XGControls/Forms/FormResult";

type StateChangedEvent = {
    state: WindowState;
    value: any;
};

/**
 * @includeToDoc
 */
export interface IWindowable {
    titleLocKey?: string;
    title?: string;
    subtitle: string;
    isEdited?: boolean;
    stateChanged: Event<IWindowable, StateChangedEvent>;
    closed: Event<IWindowable, FormResult>;
    closing: Event<IWindowable, boolean>;
    setParametricData(parametricData: IWindowableParameters): void;
    setState(state: WindowState, value: any): void;
    getState(state: WindowState): any;
    /**Get pro element - pokud by se element nastavil na private, aby se prislo na to, kde k tomu lidi pristupuji spatne, tzn. z jinych controlu a trid na grafickou prezentaci nejakeho kontrolu*/
    getElement(): HTMLElement;
    close(result?: FormResult): Promise<boolean>;
}

export class IWindowable {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            Reflect.has(instance, "titleLocKey") &&
            Reflect.has(instance, "subtitle") &&
            Reflect.has(instance, "stateChanged") &&
            Reflect.has(instance, "closed") &&
            Reflect.has(instance, "closing") &&
            typeof instance.setParametricData === "function" &&
            typeof instance.setState === "function" &&
            typeof instance.getElement === "function" &&
            typeof instance.close === "function" &&
            typeof instance.doClose === "function";
    }
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IWindowableParameters {
}

/**
 * @includeToDoc
 */
export enum WindowState {
    active = 1,
    favorite = 2,
    closeDisabled = 3,
    previewActive = 4,
    realVisibility = 5,
    visible = 6,
}

/**
 * Rozhraní představující hlavní menu aplikace
 * @includeToDoc
 */
export interface IMainMenu {
    /**
     * Registruje nově otevřené okno, aby s ním mohlo menu dále pracovat
     * @deprecated Nahrazeno událostí UIManager.formOpened, pomocí které lze zjistit otevření nového formuláře
     * @param window Nově otevřené okno s formulářem
     */
    registerOpenedWindow(window: IWindowable): void;
}

export class IMainMenu {
    private static _methods: (keyof IMainMenu)[] = ["registerOpenedWindow"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Rozhraní pro třídu definující custom obsluhu akcí položek hlavního menu aplikace.
 * Implementace se registruje jako singleton.
 */
export interface IMainMenuItemActionHandler {
    /**
     * Obsluha akce položky menu.
     * @param menuItemParams Informace o položce menu, která má být obsloužena
     * @returns Vrací objekt s informacemi o obsloužené akci. Obsahuje příznak, zda potlačit defaultní akci.
     */
    handleAction(menuItemParams: IMainMenuItemActionParams): Promise<IMainMenuItemActionResult>;
}

export class IMainMenuItemActionHandler {
    private static _properties: (keyof IMainMenuItemActionHandler)[] = [];

    private static _methods: (keyof IMainMenuItemActionHandler)[] = [
        "handleAction",
    ];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Typ aktivované položky v hlavním menu. Slouží pro rozlišení položek při zpracování v custom akcích.
 */
export enum MainMenuItemActionType {
    form = "form",
    notificationGroup = "notificationGroup",
    logoButton = "logoButton",
}

export type MainMenuItemActionData = IMainMenuFormActionData | IMainMenuNotificationGroupActionData | MainMenuLogoButtonActionData;

/**
 * Parametry pro zpracování custom akce. Typ dat se liší podle typu aktivované položky.
 */
export interface IMainMenuItemActionParams {
    readonly itemType: MainMenuItemActionType;
    readonly itemData: MainMenuItemActionData;
}

/**
 * Parametry pro custom obsluhu formulářové položky hlavního menu aplikace
 */
export interface IMainMenuFormActionData {
    /**
     * ID položky menu
     */
    readonly menuId: number;
    /**
     * Typ položky menu
     */
    readonly menuItemType: MenuItemType;
    /**
     * Kód formuláře (nepovinný)
     */
    readonly formCode?: string;
    /**
     * Kód vytvářející alias pro položku menu (nepovinný)
     */
    readonly code?: string;
    /**
     * ID profilu formuláře (nepovinný)
     */
    readonly profileId?: number;
    /**
     * Způsob otevření formuláře (nepovinný)
     */
    readonly openMode?: FormOpenMode;
    /**
     * Příznak určující, zda byla aktivována pravá strana (fungující jako složka) kombinovaného tlačítka.
     * Pro položky, které nejsou typu kombinovaného tlačítka, je vždy false.
     */
    readonly fromSideButton: boolean;
}

/**
 * Parametry pro custom obsluhu položky skupiny notifikací hlavního menu aplikace
 */
export interface IMainMenuNotificationGroupActionData {
    /**
     * Id notifikační skupiny.
     */
    readonly id: string;
}

/**
 * Parametry pro custom obsluhu tlačítka loga hlavního menu aplikace. Neposkytuje žádné parametry.
 */
export type MainMenuLogoButtonActionData = Readonly<{}>;

/**
 * Výsledné informace o obsloužené akci položky hlavního menu aplikace
 */
export interface IMainMenuItemActionResult {
    /**
     * Příznak, zda se má potlačit defaultní obsluha položky menu
     */
    readonly preventDefault: boolean;
    /**
     * Příznak, zda se má potlačit defaultní otevření formuláře.
     * Má význam jen v případě, kdy je preventDefault nastavený na false.
     * Pokud není nastaveno, neovlivňuje výchozí chování, stejně jako nastavení na false.
     */
    readonly preventFormDefault?: boolean;
}

/**
 * Kontext formuláře
 * @includeToDoc
 */
export type FormContext = {
   /** Seznam rolí, které umožnily načtení položky menu (nepovinný) */
   readonly roles?: readonly string [];

   /** Verze nastavení práv, se kterou byl formulář načten. */
   readonly rightsSettingsVersion?: string;
};

/**
 * Kontext framu. Existuje primárně kvůli transakčnímu kontextu, za který nezodpovídá `ownerForm`, ale frame jako takový.
 * @includeToDoc
 * Při rozšíření, zvážit jestli platí předpoklad v OWObject.createObject, že se kontext nezmění ani když objekt bude předán na jiný frame.
 */
export type FrameContext = FormContext & {
   /**
    * Funkce vracející ID transakčního kontextu (nebo `undefined`, pokud kontext dosud není vytvořen).
    * Pozn. Musí to být funkce, neboť kontext se může nad týmž framem dynamicky měnit.
    * Například, když dojde k uložení dat (transakční kontext se "dokončí", změny se zperzistentní a kontext je zrušen) a následně se znovu spustí editace (založí se kontext nový).
    */
   getTransactionContextId?: () => string | undefined;
};

/** Kontext objektu
 * Používá se interně v OWObjectu, ale je potřeba ho předat při vytváření nové instance. V podstatě je to samé jako {@link IFrameContext},
 * jen jméno více odpovídá kontextu použití.
 */
export type ObjectContext = FrameContext;

/**
 * Definuje nastavení, skrze které je možné přizpůsobit chování navigačního modulu.
 */
export interface IMenuNavigationConfig {
    /**
     * Pokud je nastavené na true, spustí ságy navigačního submodulu menu.
     * Pokud je nastaveno na false, ságy navigačního submodulu se nespustí, čímž se efektivně vypne navigace pomocí klávesnice.
     * To umožňuje vytvořit si zcela vlastní navigaci pomocí custom ság aniž by kolidovaly s výchozí implementací. Reducer modulu
     * je zapojený stále a je možné ho využít.
     *
     * Pokud je hodnota nastavena na false, všechna další nastavení se ignorují, protože se týkají přizpůsobení
     * výchozí implementace.
     */
    readonly enableNavigation: boolean;

    /**
     * Umožňuje vypnutí doplňujících funkcionalit navigace. Pokud není specifikované, všechny vlastnosti jsou zapnuté.
     */
    readonly optionalFeatures?: {
        /**
         * Povoluje funkcionalitu automatického otevření submenu pomocí pravé šipky a automatické zavření pomocí levé šipky nebo klávesy escape.
         *
         * Pokud je funkcionalita vypnutá, je nutné ošetřit uzly složek, aby neměly nastaveného pravého souseda. V opačném případě se může zaměření
         * dostat na prvek, který není vykreslený a tím znemožnit další navigaci.
         *
         * Pokud není specifikováno, je funkcionalita zapnutá.
         */
        readonly enableSubmenuAutoOpenAndClose?: boolean;

        /**
         * Povoluje funkcionalitu automatického zavření menu pomocí levé šipky nebo klávesy escape.
         * Pokud není specifikováno, je funkcionalita zapnutá.
         */
        readonly enableMenuAutoClose?: boolean;

        /**
         * Povoluje funkcionalitu automatického zaměření otevřeného nebo přepnutého formuláře.
         * Pokud není specifikováno, je funkcionalita zapnutá.
         */
        readonly enableAutoFocusOpenedForms?: boolean;
    };

    /**
     * Umožňuje nadefinovat kdy a jak se mají generovat navigační uzly pro jednotlivé části.
     * Pokud není specifikováno, použije se výchozí chování.
     */
    readonly nodeDefinitions?: NodeDefinitions;
}

/**
 * Obsahuje nastavení generování uzlů pro jednotlivé části hlavního menu.
 */
export type NodeDefinitions = {
    /**
     * Nastavení pro část menu panelu. Součástí je i část formulářů, protože mají sdílenou datovou závislost.
     * Pokud není specifikováno, použije se výchozí chování.
     */
    readonly menuPanel?: NodeDefinitionConfig;

    /**
     * Nastavení pro část Rychlý přístup.
     * Pokud není specifikováno, použije se výchozí chování.
     */
    readonly quickAccess?: NodeDefinitionConfig;

    /**
     * Nastavení pro část výsledků vyhledávání.
     * Pokud není specifikováno, použije se výchozí chování.
     */
    readonly searchResults?: NodeDefinitionConfig;

    /**
     * Nastavení pro část seznamu notifikací.
     * Pokud není specifikováno, použije se výchozí chování.
     */
    readonly notificationList?: NodeDefinitionConfig;

    /**
     * Nastavení pro část menu uživatele.
     * Pokud není specifikováno, použije se výchozí chování.
     */
    readonly userMenu?: NodeDefinitionConfig;
};

/**
 * Nastavení generování navigačních uzlů pro jednu oblast.
 * Pokud je nastavení zadané, ale neobsahuje statické ani dynamické nastavení generování, tak se v podstatě "vypne"
 * generování uzlů pro celou oblast.
 */
export type NodeDefinitionConfig = {

    /**
     * Sága pro generování uzlů nezávislých na datech. Spouští se pouze jednou při startu navigačního modulu.
     */
    readonly staticSaga?: SagaWithReturnType;

    /**
     * Nastavení pro generování uzlů závislých na datech. Spouští se dynamicky podle změny dat.
     */
    readonly dynamic?: {
        /**
         * Pole akcí, na základě kterých se spouští generující sága. Musí obsahovat minimálně jednu položku.
         */
        readonly actionPatterns: ReadonlyArray<string | ((action: Action) => boolean)>;

        /**
         * Sága pro generování uzlů závislých na datech. Spouští se na základě akcí definovaných v actionPatterns.
         * Sága si sama musí získat potřebná data, vygenerovat uzly a vložit je do stavu.
         */
        readonly saga: SagaWithReturnType;
    };
};

export class IMenuNavigationConfig {
    private static _properties: readonly (keyof IMenuNavigationConfig)[] = ["enableNavigation"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            this._properties.every((value) => Reflect.has(instance, value));
    }
}
